import validZipcodesData from "../data/valid-zipcodes.json";

// Create a Set for O(1) lookup performance as recommended in implementation plan
const validZipcodesSet = new Set(validZipcodesData.zipcodes);

/**
 * Checks if a zipcode is in the whitelist of accepted zipcodes
 * Provides O(1) lookup performance using a Set data structure
 *
 * @param {string} zipcode - The zipcode to validate (5-digit format expected)
 * @returns {boolean} - True if zipcode is whitelisted, false otherwise
 */
export const isZipcodeWhitelisted = (zipcode) => {
  if (!zipcode || typeof zipcode !== "string") {
    return false;
  }

  // Extract 5-digit zipcode (remove +4 extension if present)
  const cleanZipcode = zipcode.trim().split("-")[0];

  // Validate format (must be exactly 5 digits)
  if (!/^\d{5}$/.test(cleanZipcode)) {
    return false;
  }

  // O(1) lookup in the Set
  return validZipcodesSet.has(cleanZipcode);
};

/**
 * Gets the total count of valid zipcodes for informational purposes
 * @returns {number} - Total number of valid zipcodes
 */
export const getValidZipcodeCount = () => {
  return validZipcodesSet.size;
};

/**
 * Enhanced zipcode validation that combines format and whitelist checks
 * This is the main function to be used in the address validation
 *
 * @param {string} zipcode - The zipcode to validate
 * @returns {object} - Validation result with details
 */
export const validateZipcode = (zipcode) => {
  const result = {
    isValid: false,
    isFormatValid: false,
    isWhitelisted: false,
    errorType: null,
  };

  if (!zipcode || typeof zipcode !== "string") {
    result.errorType = "empty";
    return result;
  }

  const cleanZipcode = zipcode.trim().split("-")[0];

  // Check format first (5-digit only)
  result.isFormatValid = /^\d{5}$/.test(zipcode.trim());

  if (!result.isFormatValid) {
    result.errorType = "format";
    return result;
  }

  // Check whitelist
  result.isWhitelisted = validZipcodesSet.has(cleanZipcode);

  if (!result.isWhitelisted) {
    result.errorType = "not_whitelisted";
    return result;
  }

  result.isValid = true;
  return result;
};
